import { CanceledError } from 'axios';

export class RequestDeadlineError extends Error {
  constructor() {
    super('The request did not finish in time.');
    this.name = 'RequestDeadlineError';
  }
}

/** Bounds the entire operation, including authentication before transport dispatch. */
export const withRequestDeadline = <T>(
  request: (signal: AbortSignal) => Promise<T>,
  timeoutMs: number,
  signal?: AbortSignal
): Promise<T> => {
  if (signal?.aborted) return Promise.reject(new CanceledError());
  if (!Number.isFinite(timeoutMs))
    return Promise.reject(new RangeError('Invalid request deadline'));
  if (timeoutMs <= 0) return Promise.reject(new RequestDeadlineError());

  return new Promise<T>((resolve, reject) => {
    const controller = new AbortController();
    const expiresAt = Date.now() + timeoutMs;
    let settled = false;
    const cleanup = () => {
      clearTimeout(timer);
      signal?.removeEventListener('abort', cancel);
    };
    const fail = (error: unknown) => {
      if (settled) return;
      settled = true;
      cleanup();
      controller.abort();
      reject(error);
    };
    const cancel = () => fail(new CanceledError());
    const timer = setTimeout(() => fail(new RequestDeadlineError()), timeoutMs);
    signal?.addEventListener('abort', cancel, { once: true });

    let pending: Promise<T>;
    try {
      pending = request(controller.signal);
    } catch (error) {
      fail(error);
      return;
    }
    pending.then((value) => {
      if (settled) return;
      if (Date.now() >= expiresAt) {
        fail(new RequestDeadlineError());
        return;
      }
      settled = true;
      cleanup();
      resolve(value);
    }, fail);
  });
};
